import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../../shared/design-system/jalali-date-input.component';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { DynamicEntity } from '../dynamic-api.service';
import {
  FORM_FIELD_TYPES,
  FormField,
  FormFieldLookupFilter,
  FormFieldOption,
  FormFieldType,
  FormSchemaModel,
  fieldHasLookup,
  fieldHasOptions,
  readFieldDefaultValue,
  readFieldHelpText,
  readFieldLookupDisplayField,
  readFieldLookupEntityId,
  readFieldLookupFilter,
  readFieldReadonly,
  readFieldSpan,
  readLocalizedText,
  suggestFieldKey,
  writeLocalizedText
} from '../../../shared/dynamic-form/form-schema.models';
import { FormBuilderComputedPanelComponent } from './form-builder-computed-panel.component';
import { FormBuilderMatrixPanelComponent } from './form-builder-matrix-panel.component';
import { FormBuilderRepeatablePanelComponent } from './form-builder-repeatable-panel.component';
import { FormBuilderEditCultureService } from './form-builder-edit-culture.service';
import { FormBuilderLocalizedInputComponent } from './form-builder-localized-input.component';
import { FormBuilderOptionsEditorComponent } from './form-builder-options-editor.component';
import { FormBuilderValidationPanelComponent } from './form-builder-validation-panel.component';
import { FormBuilderVisibilityPanelComponent } from './form-builder-visibility-panel.component';

interface SelectOption<T = string> {
  label: string;
  value: T;
}

const NOW_DEFAULT_TOKEN = '__now__';

@Component({
  standalone: true,
  selector: 'ain-form-builder-property-panel',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TextareaModule,
    TabsModule,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    FormBuilderValidationPanelComponent,
    FormBuilderVisibilityPanelComponent,
    FormBuilderComputedPanelComponent,
    FormBuilderLocalizedInputComponent,
    FormBuilderOptionsEditorComponent,
    FormBuilderRepeatablePanelComponent,
    FormBuilderMatrixPanelComponent
  ],
  template: `
    <aside class="property-panel card">
      <div class="header">
        <h3>{{ t('dynamic.builder.properties.title') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.builder.properties.infoTitle'"
          [descriptionKey]="'dynamic.builder.properties.infoDescription'"
          [bulletKeys]="['dynamic.builder.properties.infoBullet']" />
      </div>

      @if (!field()) {
        <p class="empty">{{ t('dynamic.builder.properties.empty') }}</p>
      } @else {
        <p-tabs [value]="activeTab()" (valueChange)="onTabChange($event)">
          <p-tablist>
            <p-tab value="general">{{ t('dynamic.builder.properties.tab.general') }}</p-tab>
            <p-tab value="validation">{{ t('dynamic.builder.properties.tab.validation') }}</p-tab>
            <p-tab value="visibility">{{ t('dynamic.builder.properties.tab.visibility') }}</p-tab>
            <p-tab value="computed">{{ t('dynamic.builder.properties.tab.computed') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="general">
              <div class="form-grid">
                <ain-form-builder-localized-input
                  [label]="t('dynamic.builder.properties.label')"
                  [text]="field()!.label"
                  [disabled]="!canEdit()"
                  (textChange)="updateLabelText($event)" />

                <ain-form-field [label]="t('dynamic.builder.properties.key')">
                  <input
                    pInputText
                    [ngModel]="field()!.key"
                    [disabled]="!canEdit()"
                    (ngModelChange)="updateKey($event)" />
                  @if (keyError()) {
                    <small class="error">{{ keyError() }}</small>
                  }
                </ain-form-field>

                <ain-form-field [label]="t('dynamic.builder.properties.type')">
                  <p-select
                    [options]="fieldTypeOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [ngModel]="field()!.type"
                    [disabled]="!canEdit()"
                    appendTo="body"
                    (ngModelChange)="updateType($event)" />
                </ain-form-field>

                <ain-form-builder-localized-input
                  [label]="t('dynamic.builder.properties.placeholder')"
                  [text]="field()!.placeholder"
                  [disabled]="!canEdit()"
                  (textChange)="updatePlaceholderText($event)" />

                <ain-form-builder-localized-input
                  [label]="t('dynamic.builder.properties.helpText')"
                  [text]="field()!.helpText"
                  [multiline]="true"
                  [rows]="2"
                  [disabled]="!canEdit()"
                  (textChange)="updateHelpTextValue($event)" />

                <div class="checkbox-row">
                  <p-checkbox
                    binary
                    [ngModel]="!!field()!.required"
                    [disabled]="!canEdit()"
                    (ngModelChange)="updateRequired($event)" />
                  <label>{{ t('dynamic.builder.properties.required') }}</label>
                </div>

                <div class="checkbox-row">
                  <p-checkbox
                    binary
                    [ngModel]="readonlyValue()"
                    [disabled]="!canEdit()"
                    (ngModelChange)="updateReadonly($event)" />
                  <label>{{ t('dynamic.builder.properties.readonly') }}</label>
                </div>

                <div class="checkbox-row">
                  <p-checkbox
                    binary
                    [ngModel]="hiddenValue()"
                    [disabled]="!canEdit() || field()!.type === 'hidden'"
                    (ngModelChange)="updateHidden($event)" />
                  <label>{{ t('dynamic.builder.properties.hidden') }}</label>
                </div>

                @if (field()!.type === 'hidden') {
                  <ain-form-field [label]="t('dynamic.builder.properties.valueKind')">
                    <p-select
                      [options]="valueKindOptions()"
                      optionLabel="label"
                      optionValue="value"
                      [ngModel]="field()!.valueKind ?? 'string'"
                      [disabled]="!canEdit()"
                      appendTo="body"
                      (ngModelChange)="updateValueKind($event)" />
                  </ain-form-field>
                }

                @if (showDefaultValue()) {
                  <ain-form-field [label]="t('dynamic.builder.properties.defaultValue')">
                    @if (isDateType()) {
                      <div class="date-default-editor">
                        <p-select
                          [options]="dateDefaultOptions()"
                          optionLabel="label"
                          optionValue="value"
                          [ngModel]="dateDefaultMode()"
                          [disabled]="!canEdit()"
                          appendTo="body"
                          (ngModelChange)="updateDateDefaultMode($event)" />
                        @if (dateDefaultMode() === 'literal') {
                          <ain-date-input
                            [ngModel]="defaultValue()"
                            [disabled]="!canEdit()"
                            (ngModelChange)="updateDefaultValue($event)" />
                        }
                      </div>
                    } @else if (isBooleanType()) {
                      <p-select
                        [options]="booleanOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="defaultValue()"
                        [disabled]="!canEdit()"
                        appendTo="body"
                        (ngModelChange)="updateDefaultValue($event)" />
                    } @else {
                      <input
                        pInputText
                        [ngModel]="defaultValue()"
                        [disabled]="!canEdit()"
                        (ngModelChange)="updateDefaultValue($event)" />
                    }
                  </ain-form-field>
                }

                @if (showSpan()) {
                  <ain-form-field [label]="t('dynamic.builder.properties.columnSpan')">
                    <p-inputNumber
                      [ngModel]="columnSpanValue()"
                      [disabled]="!canEdit()"
                      [min]="1"
                      [max]="12"
                      [useGrouping]="false"
                      (ngModelChange)="updateColumnSpan($event)" />
                  </ain-form-field>
                }

                @if (showOptions()) {
                  <ain-form-builder-options-editor
                    [options]="field()!.options ?? []"
                    [canEdit]="canEdit()"
                    (optionsChange)="updateOptions($event)" />
                }

                @if (showLookup()) {
                  <section class="lookup-block">
                    <ain-info-hint
                      [titleKey]="'dynamic.builder.lookup.infoTitle'"
                      [descriptionKey]="'dynamic.builder.lookup.infoDescription'"
                      [bulletKeys]="['dynamic.builder.lookup.infoBullet']" />
                    <ain-form-field [label]="t('dynamic.builder.lookup.entity')">
                      <p-select
                        [options]="entityOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="lookupEntityId()"
                        [disabled]="!canEdit()"
                        [filter]="true"
                        [showClear]="true"
                        appendTo="body"
                        (ngModelChange)="updateLookupEntity($event)" />
                    </ain-form-field>
                    <ain-form-field [label]="t('dynamic.builder.lookup.displayField')">
                      <p-select
                        [options]="lookupFieldOptions()"
                        optionLabel="label"
                        optionValue="value"
                        [ngModel]="lookupDisplayField()"
                        [disabled]="!canEdit() || !lookupEntityId()"
                        [filter]="true"
                        [showClear]="true"
                        appendTo="body"
                        (ngModelChange)="updateLookupDisplayField($event)" />
                    </ain-form-field>

                    <div class="lookup-filter-header">
                      <strong>{{ t('dynamic.builder.lookup.filters') }}</strong>
                      @if (canEdit()) {
                        <button pButton type="button" text icon="pi pi-plus" [label]="t('dynamic.builder.lookup.addFilter')" (click)="addLookupFilter()"></button>
                      }
                    </div>
                    @for (filterRow of lookupFilters(); track $index; let idx = $index) {
                      <div class="lookup-filter-row">
                        <ain-form-field [label]="t('dynamic.builder.lookup.filterField')">
                          <p-select
                            [options]="lookupFieldOptions()"
                            optionLabel="label"
                            optionValue="value"
                            [ngModel]="filterRow.fieldKey"
                            [disabled]="!canEdit() || !lookupEntityId()"
                            [filter]="true"
                            [showClear]="true"
                            appendTo="body"
                            (ngModelChange)="updateLookupFilter(idx, 'fieldKey', $event)" />
                        </ain-form-field>
                        <ain-form-field [label]="t('dynamic.builder.lookup.filterOperator')">
                          <p-select
                            [options]="lookupFilterOperatorOptions()"
                            optionLabel="label"
                            optionValue="value"
                            [ngModel]="filterRow.op"
                            [disabled]="!canEdit()"
                            appendTo="body"
                            (ngModelChange)="updateLookupFilter(idx, 'op', $event)" />
                        </ain-form-field>
                        <ain-form-field [label]="t('dynamic.builder.lookup.filterValue')">
                          <input
                            pInputText
                            [ngModel]="filterRow.value ?? ''"
                            [disabled]="!canEdit()"
                            (ngModelChange)="updateLookupFilter(idx, 'value', $event)" />
                        </ain-form-field>
                        @if (canEdit()) {
                          <button pButton type="button" severity="danger" text icon="pi pi-trash" (click)="removeLookupFilter(idx)"></button>
                        }
                      </div>
                    }
                  </section>
                }

                @if (showRepeatable()) {
                  <ain-form-builder-repeatable-panel
                    [field]="field()!"
                    [canEdit]="canEdit()"
                    (fieldChange)="fieldChange.emit($event)" />
                }

                @if (showMatrix()) {
                  <ain-form-builder-matrix-panel
                    [field]="field()!"
                    [canEdit]="canEdit()"
                    (fieldChange)="fieldChange.emit($event)" />
                }

                @if (showRichText()) {
                  <ain-form-field [label]="t('dynamic.builder.richText.maxLength')">
                    <p-inputNumber
                      [ngModel]="field()!.richText?.maxLength ?? 5000"
                      [disabled]="!canEdit()"
                      [min]="1"
                      [useGrouping]="false"
                      (ngModelChange)="updateRichTextMaxLength($event)" />
                  </ain-form-field>
                }

                @if (showRating()) {
                  <ain-form-field [label]="t('dynamic.builder.rating.max')">
                    <p-inputNumber
                      [ngModel]="field()!.rating?.max ?? (field()!.type === 'nps' ? 10 : 5)"
                      [disabled]="!canEdit()"
                      [min]="1"
                      [max]="field()!.type === 'nps' ? 10 : 20"
                      [useGrouping]="false"
                      (ngModelChange)="updateRatingMax($event)" />
                  </ain-form-field>
                }

                @if (canEdit()) {
                  <div class="field-remove-footer">
                    <button
                      pButton
                      type="button"
                      severity="danger"
                      icon="pi pi-trash"
                      [label]="t('dynamic.builder.properties.removeField')"
                      (click)="fieldRemove.emit()"></button>
                  </div>
                }
              </div>
            </p-tabpanel>

            <p-tabpanel value="validation">
              <ain-form-builder-validation-panel
                [field]="field()!"
                [culture]="culture()"
                [canEdit]="canEdit()"
                (fieldChange)="fieldChange.emit($event)" />
            </p-tabpanel>

            <p-tabpanel value="visibility">
              <ain-form-builder-visibility-panel
                [schema]="schema()!"
                [culture]="culture()"
                [canEdit]="canEdit()"
                [excludeFieldKey]="field()!.key"
                [conditional]="field()!.conditional"
                (conditionalChange)="updateConditional($event)" />
            </p-tabpanel>

            <p-tabpanel value="computed">
              <ain-form-builder-computed-panel
                [field]="field()!"
                [schema]="schema()!"
                [culture]="culture()"
                [canEdit]="canEdit()"
                (fieldChange)="fieldChange.emit($event)" />
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
      }
    </aside>
  `,
  styles: [`
    .property-panel { display: grid; gap: 12px; align-content: start; min-width: 280px; }
    .header { display: grid; gap: 8px; }
    .header h3 { margin: 0; }
    .empty { margin: 0; color: #64748b; }
    .form-grid { display: grid; gap: 10px; }
    .checkbox-row { display: flex; align-items: center; gap: 8px; }
    .options-block {
      display: grid;
      gap: 8px;
      padding: 10px;
      border: 1px dashed #cbd5e1;
      border-radius: 8px;
      background: #f8fafc;
    }
    .lookup-block { display: grid; gap: 10px; }
    .date-default-editor { display: grid; gap: 8px; }
    .lookup-filter-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .lookup-filter-row {
      display: grid;
      gap: 8px;
      grid-template-columns: 1fr 1fr 1fr auto;
      align-items: end;
    }
    .options-header { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .option-row { display: grid; grid-template-columns: 1fr 1fr auto; gap: 8px; }
    .error { color: #b91c1c; }
    .field-remove-footer {
      display: flex;
      justify-content: flex-end;
      padding-top: 8px;
      border-top: 1px solid #e2e8f0;
    }
    .card {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 12px;
      background: #fff;
    }
  `]
})
export class FormBuilderPropertyPanelComponent {
  private readonly localization = inject(LocalizationService);
  private readonly editCultureService = inject(FormBuilderEditCultureService);

  readonly field = input<FormField | null>(null);
  readonly schema = input.required<FormSchemaModel>();
  readonly culture = input.required<string>();
  readonly allFieldKeys = input<string[]>([]);
  readonly canEdit = input(true);
  readonly showSpan = input(false);

  readonly fieldChange = output<FormField>();
  readonly fieldRemove = output<void>();
  readonly entities = input<DynamicEntity[]>([]);
  readonly entityFieldMap = input<Record<string, { label: string; value: string }[]>>({});

  readonly activeTab = signal<string>('general');

  readonly fieldTypeOptions = computed<SelectOption<FormFieldType | string>[]>(() =>
    FORM_FIELD_TYPES.map(item => ({ label: this.t(item.labelKey), value: item.value }))
  );

  readonly entityOptions = computed(() =>
    this.entities().map(entity => ({
      label: `${entity.displayName ?? entity.name} (${entity.name})`,
      value: entity.name
    }))
  );

  readonly keyError = computed(() => {
    const field = this.field();
    if (!field?.key.trim()) {
      return this.t('dynamic.builder.properties.keyRequired');
    }
    const duplicates = this.allFieldKeys().filter(key => key === field.key);
    if (duplicates.length > 1) {
      return this.t('dynamic.builder.properties.keyDuplicate');
    }
    return '';
  });

  readonly labelValue = computed(() =>
    this.field() ? readLocalizedText(this.field()!.label, this.editCultureService.editCulture()) : ''
  );

  readonly placeholderValue = computed(() =>
    this.field()?.placeholder ? readLocalizedText(this.field()!.placeholder, this.editCultureService.editCulture()) : ''
  );

  readonly helpTextValue = computed(() =>
    this.field() ? readFieldHelpText(this.field()!, this.editCultureService.editCulture()) : ''
  );

  readonly readonlyValue = computed(() =>
    this.field() ? readFieldReadonly(this.field()!) : false
  );

  readonly hiddenValue = computed(() =>
    !!this.field()?.hidden || this.field()?.type === 'hidden'
  );

  readonly valueKindOptions = computed(() => [
    { label: this.t('dynamic.builder.properties.valueKind.string'), value: 'string' },
    { label: this.t('dynamic.builder.properties.valueKind.number'), value: 'number' },
    { label: this.t('dynamic.builder.properties.valueKind.boolean'), value: 'boolean' },
    { label: this.t('dynamic.builder.properties.valueKind.json'), value: 'json' }
  ]);

  readonly defaultValue = computed(() =>
    this.field() ? readFieldDefaultValue(this.field()!) : ''
  );
  readonly dateDefaultMode = computed<'none' | 'literal' | 'now'>(() => {
    const field = this.field();
    if (!field || !['date', 'datetime'].includes(field.type)) {
      return 'literal';
    }

    const normalized = this.defaultValue().trim().toLowerCase();
    if (!normalized) {
      return 'none';
    }

    return normalized === NOW_DEFAULT_TOKEN ? 'now' : 'literal';
  });

  readonly columnSpanValue = computed(() =>
    this.field() ? readFieldSpan(this.field()!) : 1
  );

  readonly lookupEntityId = computed(() =>
    this.field() ? readFieldLookupEntityId(this.field()!) : ''
  );

  readonly lookupDisplayField = computed(() =>
    this.field() ? readFieldLookupDisplayField(this.field()!) : ''
  );

  readonly lookupFilters = computed(() =>
    this.field() ? readFieldLookupFilter(this.field()!) : []
  );

  readonly lookupFieldOptions = computed(() =>
    this.lookupEntityId() ? (this.entityFieldMap()[this.lookupEntityId()] ?? []) : []
  );

  showOptions(): boolean {
    const field = this.field();
    return !!field && fieldHasOptions(field.type);
  }

  showLookup(): boolean {
    const field = this.field();
    return !!field && fieldHasLookup(field.type);
  }

  showRepeatable(): boolean {
    return this.field()?.type === 'repeatable';
  }

  showMatrix(): boolean {
    return this.field()?.type === 'matrix';
  }

  showRichText(): boolean {
    return this.field()?.type === 'richText';
  }

  showRating(): boolean {
    return ['rating', 'nps'].includes(this.field()?.type ?? '');
  }

  showDefaultValue(): boolean {
    const type = this.field()?.type;
    return !!type && !['sectionBreak', 'stepBreak', 'content', 'computed', 'file', 'signature'].includes(type);
  }

  isDateType(): boolean {
    return ['date', 'datetime'].includes(this.field()?.type ?? '');
  }

  isBooleanType(): boolean {
    return ['boolean', 'checkbox'].includes(this.field()?.type ?? '');
  }

  booleanOptions(): SelectOption[] {
    return [
      { label: this.t('common.yes'), value: 'true' },
      { label: this.t('common.no'), value: 'false' }
    ];
  }

  dateDefaultOptions(): SelectOption<'none' | 'literal' | 'now'>[] {
    return [
      { label: this.t('common.none'), value: 'none' },
      { label: this.t('dynamic.default.now'), value: 'now' },
      { label: this.t('dynamic.default.customDate'), value: 'literal' }
    ];
  }

  private patchField(mutator: (field: FormField) => FormField): void {
    const current = this.field();
    if (!current) {
      return;
    }
    this.fieldChange.emit(mutator({ ...current }));
  }

  updateLabelText(label: FormField['label']): void {
    this.patchField(field => ({
      ...field,
      label,
      key: field.key.startsWith('field') && readLocalizedText(label, this.editCultureService.editCulture()).trim()
        ? suggestFieldKey(readLocalizedText(label, this.editCultureService.editCulture()), this.allFieldKeys().filter(key => key !== field.key))
        : field.key
    }));
  }

  updateLabel(value: string): void {
    this.patchField(field => ({
      ...field,
      label: writeLocalizedText(field.label, this.editCultureService.editCulture(), value),
      key: field.key.startsWith('field') && value.trim()
        ? suggestFieldKey(value, this.allFieldKeys().filter(key => key !== field.key))
        : field.key
    }));
  }

  updateKey(value: string): void {
    this.patchField(field => ({ ...field, key: value.trim() }));
  }

  updateType(type: FormFieldType | string): void {
    this.patchField(field => {
      const next: FormField = { ...field, type };
      if (fieldHasOptions(type) && !next.options?.length) {
        next.options = [{ value: 'option_1', label: writeLocalizedText({}, this.editCultureService.editCulture(), 'Option 1') }];
      }
      if (fieldHasLookup(type)) {
        next.prefill = { source: 'lookup', path: '' };
      }
      return next;
    });
  }

  updatePlaceholderText(placeholder: FormField['placeholder']): void {
    this.patchField(field => ({ ...field, placeholder }));
  }

  updatePlaceholder(value: string): void {
    this.patchField(field => ({
      ...field,
      placeholder: writeLocalizedText(field.placeholder ?? {}, this.editCultureService.editCulture(), value)
    }));
  }

  updateHelpTextValue(helpText: FormField['helpText']): void {
    this.patchField(field => ({ ...field, helpText }));
  }

  updateHelpText(value: string): void {
    this.patchField(field => ({
      ...field,
      helpText: writeLocalizedText(field.helpText ?? {}, this.editCultureService.editCulture(), value)
    }));
  }

  updateRequired(value: boolean): void {
    this.patchField(field => ({ ...field, required: value }));
  }

  updateReadonly(value: boolean): void {
    this.patchField(field => ({ ...field, readonly: value }));
  }

  updateHidden(value: boolean): void {
    this.patchField(field => ({
      ...field,
      hidden: value || field.type === 'hidden'
    }));
  }

  updateValueKind(value: string): void {
    this.patchField(field => ({ ...field, valueKind: value }));
  }

  updateDefaultValue(value: string): void {
    this.patchField(field => ({ ...field, defaultValue: value }));
  }

  updateDateDefaultMode(mode: 'none' | 'literal' | 'now'): void {
    this.patchField(field => ({
      ...field,
      defaultValue: mode === 'none'
        ? ''
        : mode === 'now'
          ? NOW_DEFAULT_TOKEN
          : ''
    }));
  }

  updateColumnSpan(value: number | null): void {
    this.patchField(field => ({ ...field, span: value ?? 1 }));
  }

  updateLookupEntity(entityKey: string | null): void {
    this.patchField(field => ({
      ...field,
      prefill: { source: 'lookup', path: entityKey ?? '' },
      lookup: entityKey
        ? {
            targetEntityKey: entityKey,
            displayField: undefined,
            filter: []
          }
        : undefined
    }));
  }

  updateLookupDisplayField(value: string | null): void {
    this.patchField(field => ({
      ...field,
      lookup: {
        targetEntityKey: field.lookup?.targetEntityKey ?? readFieldLookupEntityId(field),
        displayField: value ?? undefined,
        filter: readFieldLookupFilter(field)
      }
    }));
  }

  lookupFilterOperatorOptions(): SelectOption[] {
    return [
      { label: this.t('dynamic.builder.lookup.operator.eq'), value: 'eq' },
      { label: this.t('dynamic.builder.lookup.operator.neq'), value: 'neq' },
      { label: this.t('dynamic.builder.lookup.operator.contains'), value: 'contains' },
      { label: this.t('dynamic.builder.lookup.operator.startsWith'), value: 'startsWith' }
    ];
  }

  addLookupFilter(): void {
    this.patchField(field => ({
      ...field,
      lookup: {
        targetEntityKey: field.lookup?.targetEntityKey ?? readFieldLookupEntityId(field),
        displayField: field.lookup?.displayField ?? readFieldLookupDisplayField(field),
        filter: [...readFieldLookupFilter(field), { fieldKey: '', op: 'eq', value: '' }]
      }
    }));
  }

  updateLookupFilter(index: number, prop: keyof FormFieldLookupFilter, value: string | null): void {
    this.patchField(field => {
      const filters = [...readFieldLookupFilter(field)];
      const current = filters[index];
      if (!current) {
        return field;
      }
      filters[index] = {
        ...current,
        [prop]: value ?? (prop === 'value' ? undefined : '')
      };
      return {
        ...field,
        lookup: {
          targetEntityKey: field.lookup?.targetEntityKey ?? readFieldLookupEntityId(field),
          displayField: field.lookup?.displayField ?? readFieldLookupDisplayField(field),
          filter: filters
        }
      };
    });
  }

  removeLookupFilter(index: number): void {
    this.patchField(field => ({
      ...field,
      lookup: {
        targetEntityKey: field.lookup?.targetEntityKey ?? readFieldLookupEntityId(field),
        displayField: field.lookup?.displayField ?? readFieldLookupDisplayField(field),
        filter: readFieldLookupFilter(field).filter((_, idx) => idx !== index)
      }
    }));
  }

  updateOptions(options: FormFieldOption[]): void {
    this.patchField(field => ({ ...field, options }));
  }

  updateConditional(conditional: FormField['conditional']): void {
    this.patchField(field => ({ ...field, conditional }));
  }

  updateRichTextMaxLength(value: number | null): void {
    this.patchField(field => ({
      ...field,
      richText: { ...(field.richText ?? {}), maxLength: value ?? 5000 }
    }));
  }

  updateRatingMax(value: number | null): void {
    this.patchField(field => ({
      ...field,
      rating: { ...(field.rating ?? {}), max: value ?? (field.type === 'nps' ? 10 : 5) }
    }));
  }

  onTabChange(value: string | number | undefined): void {
    if (typeof value === 'string') {
      this.activeTab.set(value);
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
